import { useEffect, useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import Alert from '../../components/Alert';
import Button, { BUTTON_POSITION, BUTTON_TYPE } from '../../components/Button';
import Modal, { MODAL_TYPE } from '../../components/Modal';
import SelectPassengers from './select_passengers';
import SelectDate from './select_date';
import { OrderContext } from '../../store/OrderProvider';
import { RepositoryContext } from '../../store/RepositoryProvider';
import Checkbox from '../../components/Checkbox';
import { ExcursionPickupType, Wishes } from '../../models/types';
import API from '../../server/api';
import DateFormat from '../../components/DateFormat';
import RepositoryModel from '../../components/RepositoryModel';
import log from '../../utils/logger';
import Select from '../../components/Select';
import Field from '../../components/Field';
import InputAutocomplete from '../../components/InputAutocomplete';
import { confirm } from '../../components/Confirm';
import YMaps3, { PointFeature, ymapAvailable } from '../../components/YMap';
import { UserContext } from '../../store/UserProvider';
import Schedule from '../../models/schedule/Schedule';


const StepCommonInfo = () => {
  const {t} = useTranslation(['common', 'order']);

  const userInfo = useContext(UserContext);
  const orderInfo = useContext(OrderContext);
  const repository = useContext(RepositoryContext);

  const [excursions, setExcursions] = useState([]);
  const [subdivisions, setSubdivisions] = useState([]);
  const [prices, setPrices] = useState([]);
  const [places, setPlaces] = useState([]);
  const [points, setPoints] = useState([]);
  const [addresses, setAddresses] = useState([]);

  const [location, setLocation] = useState([]);
  const [mapAddress, setMapAddress] = useState('');
  const [mapPlace, setMapPlace] = useState(null);
  const [limits, setLimits] = useState(null);

  const [schedule, setSchedule] = orderInfo.useState('schedule');
  const [excursion, setExcursion] = orderInfo.useState('excursion');
  const [errors, setErrors] = orderInfo.useState('common_errors');

  const [commonSchedule, setCommonSchedule] = orderInfo.useSession('schedule');
  const [commonExcursion, setCommonExcursion] = orderInfo.useSession('excursion');
  const [commonSubdivision, setCommonSubdivision] = orderInfo.useSession('subdivision');
  const [commonDate, setCommonDate] = orderInfo.useSession('date');
  const [commonTime, setCommonTime] = orderInfo.useSession('time');
  const [commonPassengers, setCommonPassengers] = orderInfo.useSession('passengers');
  const [commonWishes, setCommonWishes] = orderInfo.useSession('wishes');
  const [commonPlace, setCommonPlace] = orderInfo.useSession('place');
  const [commonPassportRequired, setCommonPassportRequired] = orderInfo.useSession('passport_required');
  const [commonComment, setCommonComment] = orderInfo.useSession('comment');
  const [commonAddress, setCommonAddress] = orderInfo.useSession('address');
  const [paymentOrder] = orderInfo.useSession('order');

  const [userPointSession] = userInfo.useSession('point');


  const [showSelectPassengers, setShowSelectPassengers] = useState(false);
  const handleSelectPassengersClose = () => {
    setShowSelectPassengers(false);
    updateScheduleLimits();
  };
  const handleSelectPassengersShow = () => {
    setShowSelectPassengers(true);
    updateScheduleLimits();
  };

  const [showSelectDate, setShowSelectDate] = useState(false);
  const handleSelectDateClose = () => setShowSelectDate(false);
  const handleSelectDateShow = () => setShowSelectDate(true);


  const [showSelectMap, setShowSelectMap] = useState(false);
  const handleSelectMapClose = () => setShowSelectMap(false);
  const handleSelectMapShow = () => {
    setShowSelectMap(true);
    if (excursion?.pickupType === ExcursionPickupType.HOME) {
      setMapAddress(commonAddress);
    } else if (excursion?.pickupType === ExcursionPickupType.PLACE && points.length > 0) {
      setMapAddress(points.find((p) => p.meta?.place?.id === commonPlace)?.meta?.place?.title);
    }
  };

  const handleApplyDate = (day, time, schedule) => {
    setShowSelectDate(false);
    let date = DateFormat.Request({ date: day });
    fillSchedule({ date, time, scheduleId: schedule });
  };


  const handleChangeExcursion = (excursionId) => {
    fillSchedule({ excursionId });
  };


  const handleChangeSubdivision = (subdivisionId) => {
    setCommonSubdivision(subdivisionId);
  };


  const handleCloseError = (uid) => {
    let _errors = errors;
    orderInfo.clearError('common_errors', uid);

    if (uid) {
      let idx = _errors.findIndex(error => error.uid === uid);
      if (idx >= 0) {
        _errors.splice(idx, 1);
      }
    } else {
      _errors = [];
    }

    setErrors(_errors);
  };


  const fillSchedule = async ({ date = commonDate, time = commonTime, scheduleId = commonSchedule, excursionId = commonExcursion, placeId = commonPlace, subdivisionId = commonSubdivision }) => {
    date = date || commonDate;
    time = time || commonTime;
    excursionId = excursionId || commonExcursion;
    placeId = placeId || commonPlace;
    subdivisionId = subdivisionId || commonSubdivision;

    let schedule = null;
    let excursion = null;
    let excursions = [];
    let subdivision = null;
    let subdivisions = [];
    let prices = [];
    let places = [];
    let place = null;
    let limits = null;

    try {
      schedule = (await repository.getModel('Schedule', scheduleId)).value;

      if (schedule) {
        excursions = schedule.excursions.filter((excursion) => excursion.subdivisions.find((subdivision) => subdivision.subdivision.id === subdivisionId)).map((excursion) => ({ value: excursion.excursion.id, text: excursion.excursion.title }));
        excursion = (await repository.getModel('Excursion', excursionId)).value;

        limits = schedule.limit;

        if (excursion) {
          subdivisions = excursion.subdivisions.map((subdivision) => ({ value: subdivision.subdivision.id, text: subdivision.subdivision.title }));
          subdivision = excursion.subdivisions.find((subdivision) => subdivision.subdivision.id === subdivisionId);

          if (subdivision) {
            prices = [...subdivision.prices];
            prices = prices.map((p) => ({ ...p, partialPaymentAllowed: subdivision.partialPaymentAllowed}));

            if (schedule.route) {
              places = schedule.getPlaces().map((place) => ({ value: place.place.id, text: place.titleTime }));
              const route = (await repository.getModel('Route', schedule.route?.id)).value;
              const routerSubdivision = route?.subdivisions.find((subdivision) => subdivision.subdivision.id === subdivisionId);
              if (routerSubdivision) {
                setPoints(routerSubdivision.places.map((p, i) => new PointFeature({ id: i, longitude: p.place.location.coordinates[0], latitude: p.place.location.coordinates[1], label: p.place.location.title, meta: p})));
              }

              place = schedule.getPlaces().find((place) => place.place.id === commonPlace);
            }
          }
        };
      }
    } catch(e) {
      log.error(e);
      orderInfo.addError('common_errors', 'select_excursion', t('order:common_info.error.select_excursion'), e.message);
    }


    let newPassengers = [...commonPassengers.filter((passenger) => prices.find((price) => passenger.excursion_price === price.id))];
    let removedPassengers = [...commonPassengers.filter((passenger) => !prices.find((price) => passenger.excursion_price === price.id))];
    if (removedPassengers.length > 0) {
      if (!(await confirm(t('order:common_info.confirm_price_not_exist', {prices: removedPassengers.map((passenger) => passenger.price?.title).toString()}), t('common:button.yes'), t('common:button.no')))) {
        setCommonExcursion(commonExcursion);
        return;
      }
    }

    setLimits(limits);
    setCommonDate(date);
    setCommonTime(time);
    setCommonSchedule(scheduleId);
    setCommonPassengers(newPassengers);
    setSchedule(schedule);
    setExcursion(excursion);
    setCommonExcursion(excursion && excursion.id ? excursion.id : '');
    setExcursions(excursions);
    setSubdivisions(subdivisions);
    setCommonSubdivision(subdivision && subdivision.subdivision.id ? subdivision.subdivision.id : '');
    setPrices(prices);
    orderInfo.setPrices(prices);
    setPlaces(places);

    placeId = place ? place.place.id : ((schedule && schedule.getPlaces().length > 0) ? schedule.getPlaces()[0].place.id : '');
    setCommonPlace(placeId);

    updateScheduleLimits(scheduleId, limits);

    if (!placeId) {
      repository.getModel('Point', userPointSession).then((model) => {
        if (model?.value) {
          setLocation(model.value.location?.coordinates);
        }
      });
    } else {
      repository.getModel('Place', placeId).then((model) => {
        if (model?.value) {
          setLocation(model.value.location?.coordinates);
        }
      });
    }
  };


  const handleSelectPassengers = ({ passengers, wishes }) => {
    handleSelectPassengersClose();
    setCommonPassengers(passengers);
    setCommonWishes(wishes);
  };


  const updateScheduleLimits = async (scheduleId = commonSchedule, _limits = null) => {
    if (!_limits) {
      try {
        let schedule = await repository.request(API.schedule.schedule, { id: scheduleId }, Schedule);

        if (schedule) {
          _limits = schedule.limit;
        }
      } catch(e) {
        log.error(e);
        orderInfo.addError('common_errors', 'select_excursion', t('order:common_info.error.select_excursion'), e.message);
      }

    }

    let passengerCount = 0;
    for (let i = 0; i < commonPassengers.length; ++i) {
      const price = (await repository.getModel('Price', commonPassengers[i].price)).value;

      if (price && !price.noSeat) {
        passengerCount += commonPassengers[i].count;
      }
    }
    
    const limitSeats = _limits ? _limits.value - _limits.cntSeats : 0;

    if (_limits && _limits.stopSelling && passengerCount > limitSeats) {
      if (!orderInfo.hasError('common_errors', 'not_enough_seat')) {
        orderInfo.addError('common_errors', 'not_enough_seat', t('order:error.not_enough_seat'), t('order:error.not_enough_seat_description'));
      }
    } else {
      orderInfo.removeError('common_errors', 'not_enough_seat');
    }

    setLimits(_limits);
  };

  const handleWishes = (wish, checked) => {
    let orderWishes = [...commonWishes];
    let wishIdx = orderWishes.indexOf(wish);

    if (!checked) {
      if (wishIdx >= 0) {
        orderWishes.splice(wishIdx, 1);
      }
    } else {
      if (wishIdx === -1) {
        orderWishes.push(wish);
      }
    }

    setCommonWishes(orderWishes);
  };


  const handleApplyPlace = () => {
    if (mapPlace) {
      setCommonPlace(mapPlace.meta?.place?.id);
    } else if (mapAddress) {
      setCommonAddress(mapAddress);
    }
    handleSelectMapClose();
  };


  useEffect(() => {
    if (commonAddress) {
      repository.list(API.geo.address, { address: commonAddress }, Object).then((addresses) => {
        setAddresses(addresses ? addresses[1].map((address) => address.name) : []);
        return addresses;
      }).catch((e) => {
        log.error(e);
        setAddresses([]);
        return [];
      });
    } else {
      setAddresses([]);
    }
  }, [commonAddress]);

  useEffect(() => {
    repository.getModel('Place', commonPlace).then((model) => {
      if (model?.value) {
        setLocation(model.value.location?.coordinates);
      }
    });
  }, [commonPlace]);

  useEffect(() => {
    fillSchedule({
      date: commonDate,
      time: commonTime,
      scheduleId: commonSchedule,
      excursionId: commonExcursion,
      placeId: commonPlace,
      subdivisionId: commonSubdivision
    });
  }, []);


  return (
    <>
      <div>
        {errors.map((error, i) => (
          <Alert type={error.type} key={error.uid ?? i} title={error.title} dismissible={true} onClose={() => handleCloseError(error.uid)}>{error.message}</Alert>
        ))}

        <Field title={t('order:common_info.excursion')}>
          <Select
            value={commonExcursion}
            onSelect={handleChangeExcursion}
            options={excursions}
            disabled={!!paymentOrder}
          />
        </Field>

        {subdivisions.length <= 0 && (
          <Field title={t('order:common_info.subdivision')}>
            <Select
              value={commonSubdivision}
              onSelect={handleChangeSubdivision}
              options={subdivisions}
              disabled={!!paymentOrder}
            />
          </Field>
        )}

        {excursion && excursion.passportRequired && (
          <Field title={t('order:common_info.passport_data')}>
            <Checkbox.Slide title={t('order:common_info.put_client')} checked={!commonPassportRequired} onChange={(value, checked) => setCommonPassportRequired(!checked)} disabled={!!paymentOrder}/>
          </Field>
        )}

        <Field title={t('order:common_info.date_departure')}>
          <Button variant={BUTTON_TYPE.SELECT} onClick={handleSelectDateShow} disabled={!!paymentOrder}>
            <DateFormat.Day date={commonDate} />, {commonTime}
          </Button>
        </Field>

        <Field title={t('order:common_info.passengers')}>
          {excursion && excursion.passportRequired && commonPassportRequired && (
            <>
              {commonPassengers.reduce((count, passenger) => count + passenger.passports.reduce((count, passport) => count + (passport && passport.fullName && passport.number ? 1 : 0), 0), 0) === commonPassengers.reduce((count, passenger) => count + passenger.count, 0) ? (
                <span className="ready-note">{t('order:common_info.ready_passport')}</span>
              ) : (
                <span className="alert-note">{t('order:common_info.passport_required')}</span>
              )}
            </>
          )}
          <Button variant={BUTTON_TYPE.SELECT} onClick={handleSelectPassengersShow} disabled={!!paymentOrder}>
            {Object.values(commonPassengers.reduce((p, passenger) => {
              p[passenger.price] = p[passenger.price] ?? {excursion_price: passenger.excursion_price, price: passenger.price, count: 0, ages: []};
              p[passenger.price].count += passenger.count;
              p[passenger.price].ages.push(...(passenger.ages ?? []));
              return p;
            }, {})).map((passenger) => (
              <div className="passenger-price" key={passenger.excursion_price}>{passenger.count} <RepositoryModel type="Price" uid={passenger.price} render={(model) => model.title} /></div>
            ))}
          </Button>
          <Checkbox.Group>
            {Object.values(Wishes).map((wish) => (
              <Checkbox.Button key={wish} title={t(`order:wishes.${wish}`)} checked={commonWishes.indexOf(wish) >= 0} value={wish} onChange={handleWishes} disabled={!!paymentOrder}/>
            ))}
          </Checkbox.Group>
        </Field>

        {excursion && excursion.pickupType === ExcursionPickupType.PLACE && (
          <Field title={t('order:common_info.place_time')}>
            <Select
              value={commonPlace}
              onSelect={setCommonPlace}
              empty={{ value: -1, text: t('order:common_info.none')}}
              options={places}
              disabled={!!paymentOrder}
            />
          </Field>
        )}

        {excursion && excursion.pickupType === ExcursionPickupType.HOME && (
          <Field title={t('order:common_info.place')}>
            <InputAutocomplete
              list={addresses}
              value={commonAddress}
              onInput={setCommonAddress}
              asisText={false}
            />
          </Field>
        )}

        {ymapAvailable && (
          <Button className="mb-4" variant={BUTTON_TYPE.SELECTED} onClick={handleSelectMapShow} disabled={!!paymentOrder}>{t('order:common_info.select_place')}</Button>
        )}

        <Field title={t('order:common_info.comment')}>
          <textarea rows="3" onChange={(e) => setCommonComment(e.target.value)} value={commonComment}/>
        </Field>
      </div>


      <Modal show={showSelectPassengers} onHide={handleSelectPassengersClose}>
        <Modal.Header>{t('order:select_passengers.title')}</Modal.Header>
        <Modal.Body>
          {showSelectPassengers && (
            <SelectPassengers
              prices={prices}
              limits={{seats: (limits ? limits.value - limits.cntSeats : 0), stopSelling: limits ? limits.stopSelling : false}}
              passportRequired={commonPassportRequired}
              passengers={commonPassengers}
              wishes={commonWishes}
              onApply={handleSelectPassengers}
              onCancel={handleSelectPassengersClose}
            />
          )}
        </Modal.Body>
      </Modal>

      <Modal show={showSelectDate} onHide={handleSelectDateClose}>
        <Modal.Header>{t('order:select_date.title')}</Modal.Header>
        <Modal.Body>
          {showSelectDate && (
            <SelectDate onApply={handleApplyDate} onCancel={handleSelectDateClose}/>
          )}
        </Modal.Body>
      </Modal>

      <Modal show={showSelectMap} onHide={handleSelectMapClose} type={MODAL_TYPE.FULLSCREEN}>
        <Modal.Header>{t('order:select_place.title')}</Modal.Header>
        <Modal.Body>
          {location && (
            <>
              <YMaps3 longitude={location[0]} latitude={location[1]} points={points} zoom={15} address={points.length === 0 ? mapAddress : ''}
                onSelectPoint={(feature) => {
                  setMapPlace(feature);
                  setMapAddress(feature?.meta?.place?.title);
                }}
                onSelectAddress={(address, coordinates) => {
                  setMapAddress(address ?? '-');
                }}
              />
              <Field title={t('order:select_place.place')}>
                {mapAddress}
              </Field>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button.Panel>
            <Button position={BUTTON_POSITION.LEFT} variant={BUTTON_TYPE.PRIMARY} onClick={handleApplyPlace}>{t('order:select_place.apply')}</Button>
            <Button position={BUTTON_POSITION.RIGHT} variant={BUTTON_TYPE.SECONDARY} onClick={handleSelectMapClose}>{t('common:button.cancel')}</Button>
          </Button.Panel>
        </Modal.Footer>
      </Modal>
    </>
  )
}

StepCommonInfo.propTypes = {
  onChange: PropTypes.func,
  onComplete: PropTypes.func,
  onError: PropTypes.func,
};

export default StepCommonInfo;